import React, { useEffect, useMemo, useRef, useState } from 'react';
import API from '../services/api';
import { Row, Col, Button, Spinner, Table, Badge, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './VendorOverview.css';
import { formatVendorId } from '../utils/idFormat';
import { downloadVendorAgreementPdf, openVendorAgreementPreview } from '../utils/pdfDocs';

const MAX_LOGO_BYTES = 1024 * 1024 * 2; // 2MB
const SIGN_PAD_WIDTH = 640;
const SIGN_PAD_HEIGHT = 220;

const getLogoSrc = (logo) => {
  if (!logo) return '/assets/logo.png';
  if (typeof logo !== 'string') return '/assets/logo.png';
  if (logo.startsWith('data:image')) return logo;
  if (/^https?:\/\//i.test(logo)) return logo;
  return logo.startsWith('/') ? logo : `/${logo}`;
};

const VendorOverview = () => {
  const [vendor, setVendor] = useState(null);
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ applied: 0, approved: 0, completed: 0 });
  const [recentRequirements, setRecentRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [acceptingAgreement, setAcceptingAgreement] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signName, setSignName] = useState('');
  const [signatureError, setSignatureError] = useState('');
  const fileRef = useRef(null);
  const signCanvasRef = useRef(null);
  const signPadRef = useRef({
    drawing: false,
    moved: false,
    lastX: 0,
    lastY: 0,
  });
  const navigate = useNavigate();

  const fetchAll = async () => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const [meRes, profileRes, statsRes, recentRes] = await Promise.allSettled([
      API.get('/auth/me', { headers }),
      API.get('/vendor/profile', { headers }),
      API.get('/vendor/requirements/stats', { headers }),
      API.get('/vendor/requirements/recent', { headers }),
    ]);
    setUser(meRes.status === "fulfilled" ? (meRes.value?.data || null) : null);
    setVendor(profileRes.status === "fulfilled" ? (profileRes.value?.data || {}) : {});
    setStats(statsRes.status === "fulfilled" ? (statsRes.value?.data || { applied: 0, approved: 0, completed: 0 }) : { applied: 0, approved: 0, completed: 0 });
    setRecentRequirements(
      recentRes.status === "fulfilled" && Array.isArray(recentRes.value?.data)
        ? recentRes.value.data
        : []
    );
  };

  useEffect(() => {
    (async () => {
      try {
        await fetchAll();
      } catch (err) {
        console.error('Failed to load vendor dashboard', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!showSignModal) return undefined;
    const canvas = signCanvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#123524';
    signPadRef.current = {
      drawing: false,
      moved: false,
      lastX: 0,
      lastY: 0,
    };
    return undefined;
  }, [showSignModal]);

  const handleClickAvatar = () => fileRef.current?.click();

  const handleSelectLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) {
      alert('Logo is too large. Please upload an image under 2MB.');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        setUploadingLogo(true);
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // Send data URL to backend
        await API.put('/vendor/profile/logo', { logo: reader.result }, { headers });
        // Refresh vendor profile in UI
        const res = await API.get('/vendor/profile', { headers });
        setVendor(res.data || {});
      } catch (err) {
        console.error('Logo update failed', err);
        alert(err?.response?.data?.message || 'Failed to update logo');
      } finally {
        setUploadingLogo(false);
        e.target.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const vendorData = useMemo(() => vendor || {}, [vendor]);
  const assignedManager = vendorData.assignedManager || null;
  const financialSummary = vendorData.financialSummary || null;

  const productInterests = useMemo(() => {
    if (Array.isArray(vendorData.productInterest)) return vendorData.productInterest.filter(Boolean);
    if (typeof vendorData.productInterest === 'string') {
      return vendorData.productInterest.split(/[,|\n]/).map((s) => s.trim()).filter(Boolean);
    }
    return [];
  }, [vendorData.productInterest]);

  const completionScore = useMemo(() => {
    const has = (v) => String(v || '').trim().length > 0;
    const lat = Number(vendorData.geoLocation?.lat);
    const lng = Number(vendorData.geoLocation?.lng);
    const checks = [
      has(vendorData.companyName),
      has(vendorData.gstNumber),
      has(vendorData.address),
      has(vendorData.turnover),
      Number.isFinite(lat),
      Number.isFinite(lng),
      has(vendorData.contactName),
      has(vendorData.contactMobile),
      has(vendorData.contactEmail),
      has(vendorData.bank?.accountNumber),
      has(vendorData.documents?.gstCertificate),
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [vendorData]);

  useEffect(() => {
    setSignName(vendor?.contactName || vendor?.companyName || user?.fullName || 'Vendor');
  }, [vendor?.contactName, vendor?.companyName, user?.fullName]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant="success" />
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="text-center mt-5">
        <p className="text-danger">Could not load your vendor profile.</p>
      </div>
    );
  }

  const companyOrName = vendor.companyName || vendor.fullName || 'Vendor';
  const certStatus = user?.certificateStatus || '';
  const certReady = !!vendor.certificateUrl || vendor.certificateIssued || certStatus === 'generated';
  const logoSrc = getLogoSrc(vendor.logo);
  const vendorId = formatVendorId(vendor, user || {});
  const securityDue = Number(financialSummary?.securityDepositRequired || 0);
  const securityPaid = !!financialSummary?.securityDepositPaid;
  const creditLimit = Number(financialSummary?.creditLimit || 0);
  const availableLimit = Number(financialSummary?.availableLimit || 0);
  const utilizedLimit = Number(financialSummary?.utilizedLimit || 0);
  const dueDays = Number(financialSummary?.paymentDueDays || 0);
  const dailyPenaltyPercent = Number(financialSummary?.dailyPenaltyPercent || 0);
  const vendorAccepted = !!vendor?.agreementAcceptedByVendor;
  const adminAccepted = !!vendor?.agreementAcceptedByAdmin;
  const agreementReady = String(vendor?.agreementText || '').trim() || String(vendor?.agreementUrl || '').trim();

  const getCanvasPoint = (event) => {
    const canvas = signCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0]?.clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0]?.clientY : event.clientY;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startSignature = (event) => {
    if (event.cancelable) event.preventDefault();
    const ctx = signCanvasRef.current?.getContext('2d');
    if (!ctx) return;
    const point = getCanvasPoint(event);
    signPadRef.current = {
      drawing: true,
      moved: false,
      lastX: point.x,
      lastY: point.y,
    };
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    setSignatureError('');
  };

  const moveSignature = (event) => {
    if (event.cancelable) event.preventDefault();
    const pad = signPadRef.current;
    if (!pad.drawing) return;
    const ctx = signCanvasRef.current?.getContext('2d');
    if (!ctx) return;
    const point = getCanvasPoint(event);
    ctx.beginPath();
    ctx.moveTo(pad.lastX, pad.lastY);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    signPadRef.current = {
      drawing: true,
      moved: true,
      lastX: point.x,
      lastY: point.y,
    };
  };

  const endSignature = () => {
    signPadRef.current = {
      ...signPadRef.current,
      drawing: false,
    };
  };

  const clearSignature = () => {
    const canvas = signCanvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    signPadRef.current = {
      drawing: false,
      moved: false,
      lastX: 0,
      lastY: 0,
    };
    setSignatureError('');
  };

  const acceptAgreement = async () => {
    if (!agreementReady) return;
    const canvas = signCanvasRef.current;
    const hasSignature = !!signPadRef.current.moved;
    if (!String(signName || '').trim()) {
      setSignatureError('Signer name is required.');
      return;
    }
    if (!canvas || !hasSignature) {
      setSignatureError('Please draw your signature before accepting.');
      return;
    }
    try {
      setAcceptingAgreement(true);
      await API.post('/vendor/agreement/accept', {
        signName,
        signatureDataUrl: canvas.toDataURL('image/png'),
      });
      await fetchAll();
      setShowSignModal(false);
    } catch (err) {
      setSignatureError(err?.response?.data?.message || 'Agreement accept failed');
    } finally {
      setAcceptingAgreement(false);
    }
  };

  return (
    <div className="vendor-dashboard">
      {/* hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleSelectLogo}
      />

      {/* Welcome Banner */}
      <section className="vd-card vd-hero">
        <div className="vd-hero-main">
          <div className="vd-hero-left">
            <div className="vd-logo-wrap" role="button" onClick={handleClickAvatar} aria-label="Change logo">
              <img
                src={logoSrc}
                alt={`${companyOrName} logo`}
                onError={(e) => { e.currentTarget.src = '/assets/logo.png'; }}
              />
              {uploadingLogo ? <span className="vd-logo-loading">...</span> : null}
            </div>
            <div>
              <div className="vd-hero-title">
                <h2>{companyOrName}</h2>
                <span className={`vd-chip ${certStatus === 'generated' || vendor.certificateIssued ? 'ok' : certStatus === 'rejected' ? 'bad' : 'warn'}`}>
                  {certStatus === 'generated' || vendor.certificateIssued ? 'Certified Vendor' : certStatus === 'rejected' ? 'Verification Rejected' : 'Verification Pending'}
                </span>
              </div>
              <p className="vd-hero-sub">Vendor ID: {vendorId}</p>
              <div className="vd-hero-actions">
                <Button className="vd-btn-primary" onClick={() => navigate('/vendor/profile')}>
                  Edit Profile
                </Button>
                <Button className="vd-btn-ghost" onClick={() => navigate('/vendor/requirement')}>
                  Submit Requirement
                </Button>
              </div>
            </div>
          </div>
          <div className="vd-hero-side">
            <div className="vd-hero-score">
              <div className="vd-score-label">Compliance Score</div>
              <div className="vd-score">
                <span>{completionScore}</span>
                <small>/ 100</small>
              </div>
              <div className="vd-score-note">Based on profile completeness</div>
            </div>
            <div className="vd-hero-manager">
              <div className="vd-score-label">Assigned Manager</div>
              {assignedManager ? (
                <>
                  <div className="vd-manager-line">
                    <span className="material-symbols-outlined vd-manager-icon">person</span>
                    <span className="vd-value">{assignedManager.fullName || '—'}</span>
                  </div>
                  <div className="vd-manager-line">
                    <span className="material-symbols-outlined vd-manager-icon">call</span>
                    <span className="vd-muted small">{assignedManager.mobile || '—'}</span>
                  </div>
                  <div className="vd-manager-line">
                    <span className="material-symbols-outlined vd-manager-icon">mail</span>
                    <span className="vd-muted small">{assignedManager.email || '—'}</span>
                  </div>
                </>
              ) : (
                <div className="vd-muted">Manager not assigned yet.</div>
              )}
            </div>
          </div>
        </div>
        <div className="vd-hero-meta">
          <div className="vd-meta-item">
            <span className="vd-meta-label">GST</span>
            <span className="vd-meta-value">{vendor.gstNumber || '—'}</span>
          </div>
          <div className="vd-meta-item">
            <span className="vd-meta-label">Turnover</span>
            <span className="vd-meta-value">{vendor.turnover || '—'}</span>
          </div>
          <div className="vd-meta-item">
            <span className="vd-meta-label">Address</span>
            <span className="vd-meta-value">{vendor.address || vendor?.geoLocation?.address || '—'}</span>
          </div>
        </div>
      </section>

      {/* Stats Row */}
      <section className="vd-stats">
        <div className="vd-stat-card blue">
          <div>
            <div className="vd-stat-label">Total Applied</div>
            <div className="vd-stat-value">{stats.applied ?? 0}</div>
          </div>
          <div className="vd-stat-icon">assignment</div>
        </div>
        <div className="vd-stat-card amber">
          <div>
            <div className="vd-stat-label">Approved</div>
            <div className="vd-stat-value">{stats.approved ?? 0}</div>
          </div>
          <div className="vd-stat-icon">check_circle</div>
        </div>
        <div className="vd-stat-card green">
          <div>
            <div className="vd-stat-label">Completed</div>
            <div className="vd-stat-value">{stats.completed ?? 0}</div>
          </div>
          <div className="vd-stat-icon">verified</div>
        </div>
      </section>

      {/* Details + Sidebar */}
      <Row className="g-4">
        <Col lg={8}>
          <section className="vd-card">
            <div className="vd-card-header">
              <h3>Vendor Profile Details</h3>
              <Button variant="link" className="vd-link-btn" onClick={() => navigate('/vendor/profile')}>
                Edit Profile
              </Button>
            </div>
            <div className="vd-details">
              <div>
                <div className="vd-label">GST Number</div>
                <div className="vd-value">{vendor.gstNumber || '—'}</div>
              </div>
              <div>
                <div className="vd-label">Annual Turnover</div>
                <div className="vd-value">{vendor.turnover || '—'}</div>
              </div>
              <div className="vd-span-2">
                <div className="vd-label">Registered Address</div>
                <div className="vd-value">{vendor.address || vendor?.geoLocation?.address || '—'}</div>
              </div>
              <div>
                <div className="vd-label">Product Interests</div>
                <div className="vd-tags">
                  {productInterests.length ? productInterests.map((p) => (
                    <Badge key={p} bg="light" text="dark" className="vd-tag">{p}</Badge>
                  )) : <span className="vd-muted">—</span>}
                </div>
              </div>
              <div>
                <div className="vd-label">Certificate Status</div>
                <div className="vd-value">
                  <span className={`vd-status ${certStatus === 'generated' || vendor.certificateIssued ? 'ok' : certStatus === 'rejected' ? 'bad' : 'warn'}`}>
                    {certStatus === 'generated' || vendor.certificateIssued ? 'Verified Active' : certStatus === 'rejected' ? 'Rejected' : 'Pending'}
                  </span>
                </div>
                {certReady ? (
                  <Button
                    size="sm"
                    className="mt-2"
                    variant="outline-success"
                    href={vendor.certificateUrl || '/vendor/certificate'}
                    target={vendor.certificateUrl ? '_blank' : undefined}
                  >
                    {vendor.certificateUrl ? 'Download Certificate' : 'View Certificate'}
                  </Button>
                ) : null}
              </div>
              <div>
                <div className="vd-label">Contact</div>
                <div className="vd-value">
                  {vendor.contactName || '—'}{vendor.contactMobile ? ` • ${vendor.contactMobile}` : ''}
                </div>
              </div>
            </div>
          </section>

          {/* Recent Requirements Table */}
          <section className="vd-card mt-4">
            <div className="vd-card-header">
              <div>
                <h3>Recent Requirements</h3>
                <div className="vd-muted">Latest procurement requests</div>
              </div>
              <Button variant="outline-secondary" size="sm" onClick={() => navigate('/vendor/requirement/history')}>
                View All
              </Button>
            </div>
            <div className="table-responsive">
              <Table hover responsive className="vd-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Requirement</th>
                    <th>Quantity</th>
                    <th>Amount</th>
                    <th>Submitted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRequirements.length > 0 ? (
                    recentRequirements.map((req) => {
                      const label =
                        req.title ||
                        [req.productType, req.quantity && `${req.quantity} ${req.unit || ''}`.trim()]
                          .filter(Boolean)
                          .join(' — ');
                      return (
                        <tr key={req._id}>
                          <td className="vd-id">#{String(req._id || '').slice(-6) || '—'}</td>
                          <td>{label}</td>
                          <td>{req.quantity ? `${req.quantity} ${req.unit || ''}` : '—'}</td>
                          <td>
                            {Number(req.requirementAmount || 0) > 0
                              ? `₹ ${Number(req.requirementAmount || 0).toLocaleString('en-IN')}`
                              : '—'}
                          </td>
                          <td>{req.createdAt ? new Date(req.createdAt).toLocaleDateString() : '—'}</td>
                          <td>
                            <span className={`vd-pill ${req.status === 'approved' ? 'warn' : req.status === 'completed' ? 'ok' : 'muted'}`}>
                              {req.status || 'pending'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No recent requirements found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <div className="vd-mobile-list">
              {recentRequirements.length > 0 ? (
                recentRequirements.map((req) => {
                  const label =
                    req.title ||
                    [req.productType, req.quantity && `${req.quantity} ${req.unit || ''}`.trim()]
                      .filter(Boolean)
                      .join(' — ');
                  return (
                    <div className="vd-mobile-card" key={req._id}>
                      <div className="vd-mobile-row">
                        <strong>#{String(req._id || '').slice(-6) || '—'}</strong>
                        <span className={`vd-pill ${req.status === 'approved' ? 'warn' : req.status === 'completed' ? 'ok' : 'muted'}`}>
                          {req.status || 'pending'}
                        </span>
                      </div>
                      <div className="small">{label}</div>
                      <div className="vd-mobile-meta">
                        <span>Qty: {req.quantity ? `${req.quantity} ${req.unit || ''}` : '—'}</span>
                        <span>
                          Amount: {Number(req.requirementAmount || 0) > 0 ? `₹ ${Number(req.requirementAmount || 0).toLocaleString('en-IN')}` : '—'}
                        </span>
                        <span>Date: {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : '—'}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-muted">No recent requirements found.</div>
              )}
            </div>
          </section>
        </Col>
        <Col lg={4}>
          <section className={`vd-card vd-credit-alert ${securityPaid ? 'ok' : 'warn'}`}>
            <div className="vd-card-header">
              <h3>Security & Purchase Limit</h3>
            </div>
            <div className="vd-credit-status">
              <div className="vd-label">Security Deposit</div>
              <div className="vd-value">
                {securityPaid ? 'Paid' : 'Pending'} • {`₹ ${securityDue.toLocaleString('en-IN')}`}
              </div>
            </div>
            <div className="vd-credit-grid">
              <div>
                <div className="vd-label">Approved Limit</div>
                <div className="vd-value">{`₹ ${creditLimit.toLocaleString('en-IN')}`}</div>
              </div>
              <div>
                <div className="vd-label">Used Limit</div>
                <div className="vd-value">{`₹ ${utilizedLimit.toLocaleString('en-IN')}`}</div>
              </div>
              <div>
                <div className="vd-label">Available Limit</div>
                <div className="vd-value">{`₹ ${availableLimit.toLocaleString('en-IN')}`}</div>
              </div>
              <div>
                <div className="vd-label">Payment Schedule</div>
                <div className="vd-value">{`${dueDays} days • ${dailyPenaltyPercent.toFixed(2)}%/day delay`}</div>
              </div>
            </div>
            {!securityPaid ? (
              <div className="vd-credit-note">
                Security deposit pending hai. Requirement submit karne ke liye pehle deposit clear hona zaroori hai.
              </div>
            ) : null}
            {vendor.agreementText ? <div className="vd-credit-note">{vendor.agreementText}</div> : null}
            <div className="vd-credit-note">
              <strong>Agreement Status:</strong>{" "}
              Vendor: {vendorAccepted ? "Accepted" : "Pending"} | Admin: {adminAccepted ? "Accepted" : "Pending"}
            </div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <Button
                size="sm"
                variant={vendorAccepted ? "success" : "primary"}
                disabled={!agreementReady || vendorAccepted || acceptingAgreement}
                onClick={() => {
                  setSignatureError('');
                  setShowSignModal(true);
                }}
              >
                {vendorAccepted ? "Agreement Accepted" : acceptingAgreement ? "Accepting..." : "Accept Agreement"}
              </Button>
              <Button
                size="sm"
                variant="outline-dark"
                onClick={() =>
                  downloadVendorAgreementPdf({
                    vendor,
                    financialSummary,
                    assignedManager,
                  })
                }
              >
                Download Agreement PDF
              </Button>
              <button
                type="button"
                className="vd-agreement-link"
                style={{ background: "none", border: "none", padding: 0 }}
                onClick={() =>
                  openVendorAgreementPreview({
                    vendor,
                    financialSummary,
                    assignedManager,
                  })
                }
              >
                View Agreement
              </button>
            </div>
          </section>

          <section className="vd-card">
            <div className="vd-card-header">
              <h3>Quick Actions</h3>
            </div>
            <div className="vd-actions">
              <button className="vd-action" onClick={() => navigate('/vendor/requirement')}>
                <span className="vd-action-icon ok">description</span>
                <div>
                  <div className="vd-action-title">Submit Requirement</div>
                  <div className="vd-action-sub">Raise a new request</div>
                </div>
                <span className="vd-action-arrow">chevron_right</span>
              </button>
              <button className="vd-action" onClick={() => navigate('/vendor/profile')}>
                <span className="vd-action-icon warn">edit</span>
                <div>
                  <div className="vd-action-title">Update Profile</div>
                  <div className="vd-action-sub">Improve compliance score</div>
                </div>
                <span className="vd-action-arrow">chevron_right</span>
              </button>
              <button className="vd-action" onClick={() => navigate('/vendor/requirement/history')}>
                <span className="vd-action-icon cool">analytics</span>
                <div>
                  <div className="vd-action-title">Requirement History</div>
                  <div className="vd-action-sub">Track approvals & status</div>
                </div>
                <span className="vd-action-arrow">chevron_right</span>
              </button>
            </div>
          </section>
          <section className="vd-card vd-help">
            <div>
              <h4>Need Help?</h4>
              <p>Contact your account manager for any queries about your supply chain performance.</p>
              <Button className="vd-btn-light" onClick={() => navigate('/vendor/notifications')}>
                Contact Support
              </Button>
            </div>
          </section>
        </Col>
      </Row>

      <Modal
        show={showSignModal}
        onHide={() => {
          if (acceptingAgreement) return;
          setShowSignModal(false);
          setSignatureError('');
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sign Agreement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="vd-sign-modal-copy">
            Neeche signature draw karke accept kariye. Yehi signature agreement PDF aur preview me show hoga.
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Signer Name</Form.Label>
            <Form.Control
              value={signName}
              onChange={(e) => setSignName(e.target.value)}
              placeholder="Enter signer name"
            />
          </Form.Group>
          <div className="vd-sign-pad-wrap">
            <canvas
              ref={signCanvasRef}
              className="vd-sign-pad"
              width={SIGN_PAD_WIDTH}
              height={SIGN_PAD_HEIGHT}
              onMouseDown={startSignature}
              onMouseMove={moveSignature}
              onMouseUp={endSignature}
              onMouseLeave={endSignature}
              onTouchStart={startSignature}
              onTouchMove={moveSignature}
              onTouchEnd={endSignature}
            />
          </div>
          {signatureError ? <div className="vd-sign-error">{signatureError}</div> : null}
          <div className="vd-sign-actions">
            <Button variant="outline-secondary" onClick={clearSignature} disabled={acceptingAgreement}>
              Clear
            </Button>
            <Button variant="success" onClick={acceptAgreement} disabled={acceptingAgreement}>
              {acceptingAgreement ? 'Saving...' : 'Accept and Save Signature'}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default VendorOverview;
